import { GenieBranding } from '@/types/ui/branding';
import { Agent } from '@/types/agent/agent';

/** Portal layout type - v1 only supports grid */
export type PortalLayoutType = 'grid';

/** Configuration for portal grid layout */
export interface PortalLayoutConfig {
  columns?: 2 | 3 | 4;
  cardStyle?: 'compact' | 'detailed' | 'minimal';
  showDescriptions?: boolean;
  gridGap?: number;
}

/** Portal entity - a public page aggregating multiple genies */
export interface Portal {
  id: string;
  user_id: string;
  name: string;
  slug: string;
  description: string | null;
  branding: GenieBranding | null;
  layout_type: PortalLayoutType;
  layout_config: PortalLayoutConfig;
  meta_title: string | null;
  meta_description: string | null;
  hero_image_url: string | null;
  welcome_message: string | null;
  is_active: boolean;
  is_public: boolean;
  password_protected: boolean;
  embed_allowed: boolean;
  allowed_domains: string[] | null;
  view_count: number;
  position: number;
  created_at: string;
  updated_at: string;
}

/** Lightweight genie summary for portal list cards */
export interface PortalGenieSummary {
  agent_id: string;
  name: string;
  logoUrl: string | null;
  primaryColor: string | null;
}

/** Portal with genie count for list views */
export interface PortalWithCount extends Portal {
  genie_count: number;
  /** Lightweight genie summaries for card avatars (max ~6) */
  genies_summary?: PortalGenieSummary[];
}

/** Junction table entry linking a portal to a genie */
export interface PortalGenie {
  id: number;
  portal_id: string;
  agent_id: string;
  display_name: string | null;
  display_description: string | null;
  custom_avatar_url: string | null;
  position: number;
  is_featured: boolean;
  is_visible: boolean;
  category_label: string | null;
  created_at: string;
  /** Joined agent data when fetched with relations */
  agent?: Agent;
}

/** Portal with its associated genies */
export interface PortalWithGenies extends Portal {
  genies: PortalGenie[];
}

/** Parameters for creating a new portal */
export interface CreatePortalParams {
  name: string;
  slug?: string;
  description?: string;
  branding?: GenieBranding;
  layout_type?: PortalLayoutType;
  layout_config?: PortalLayoutConfig;
  welcome_message?: string;
}

/** Parameters for updating an existing portal */
export interface UpdatePortalParams {
  name?: string;
  slug?: string;
  description?: string;
  branding?: GenieBranding;
  layout_type?: PortalLayoutType;
  layout_config?: PortalLayoutConfig;
  meta_title?: string;
  meta_description?: string;
  hero_image_url?: string;
  welcome_message?: string;
  is_active?: boolean;
  is_public?: boolean;
  password_protected?: boolean;
  embed_allowed?: boolean;
  allowed_domains?: string[];
}

/** Parameters for adding a genie to a portal */
export interface AddGenieToPortalParams {
  portal_id: string;
  agent_id: string;
  display_name?: string;
  display_description?: string;
  custom_avatar_url?: string;
  position?: number;
  is_featured?: boolean;
  category_label?: string;
}

/** Parameters for updating a genie within a portal */
export interface UpdatePortalGenieParams {
  display_name?: string;
  display_description?: string;
  custom_avatar_url?: string;
  position?: number;
  is_featured?: boolean;
  is_visible?: boolean;
  category_label?: string;
}

/** Default layout configuration */
export const DEFAULT_LAYOUT_CONFIG: PortalLayoutConfig = {
  columns: 3,
  cardStyle: 'detailed',
  showDescriptions: true,
  gridGap: 24,
};
